import React from 'react';
import styled from 'styled-components';
// Imported from the leaf module, NOT the @ui barrel: src/ui/index.js re-exports ./inputs, and
// src/ui/text/Body4.js imports from @ui, so going through the barrel here closes a require cycle
// that leaves Body7 undefined for whichever module loads first.
import { Body7 } from '@ui/text/Body7';

// Labelled text input / textarea. `src/ui/inputs` shipped only Checkbox before this, so every
// feature that needed a text field hand-rolled a styled input with no focus ring and no label
// association. This is that field, once, with both.
//
// `hint` renders under the control and is wired to it via aria-describedby, so a screen reader
// gets the consequence sentence the sighted operator gets.

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const fieldStyles = `
  width: 100%;
  border-radius: 6px;
  padding: 9px 11px;
  background: transparent;
  font-size: 14px;
  font-family: inherit;
`;

const Input = styled.input`
  ${fieldStyles};
  border: 1px solid ${({ theme, $invalid }) => ($invalid ? theme.themeColors.error : theme.themeColors.border)};
  color: ${({ theme }) => theme.themeColors.text};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: 1px;
  }

  &:read-only {
    background: ${({ theme }) => theme.themeColors.disabled};
    cursor: default;
  }
`;

const Area = styled.textarea`
  ${fieldStyles};
  border: 1px solid ${({ theme, $invalid }) => ($invalid ? theme.themeColors.error : theme.themeColors.border)};
  color: ${({ theme }) => theme.themeColors.text};
  line-height: 1.55;
  resize: vertical;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: 1px;
  }
`;

const Hint = styled(Body7)`
  color: ${({ theme, $invalid }) => ($invalid ? theme.themeColors.error : theme.themeColors.secondaryText)};
`;

let uid = 0;
const nextId = () => {
  uid += 1;
  return `text-field-${uid}`;
};

export const TextField = ({
  label,
  value,
  onChange,
  hint,
  invalid = false,
  multiline = false,
  rows = 3,
  id,
  mono = false,
  ...rest
}) => {
  const [fallbackId] = React.useState(nextId);
  const fieldId = id || fallbackId;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const Field = multiline ? Area : Input;

  return (
    <Wrapper>
      {label && (
        <label htmlFor={fieldId}>
          <Body7>{label}</Body7>
        </label>
      )}
      <Field
        id={fieldId}
        value={value === undefined || value === null ? '' : value}
        onChange={(e) => onChange && onChange(e.target.value)}
        aria-describedby={hintId}
        aria-invalid={invalid || undefined}
        $invalid={invalid}
        rows={multiline ? rows : undefined}
        style={mono ? { fontFamily: 'monospace', fontSize: '13px' } : undefined}
        {...rest}
      />
      {hint && (
        <Hint id={hintId} $invalid={invalid}>
          {hint}
        </Hint>
      )}
    </Wrapper>
  );
};
